import { useState } from "react";
import { Copy, Link2, MessageCircle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const TONE = { active: "green", used: "navy", revoked: "slate", expired: "slate" };

function ShareBox({ url, kind, linkId }) {
  const msg = `${t(kind === "report" ? "b15.shareText" : "b12.link.shareText")} ${url}`;
  const log = (channel) => api.post(`/secure-links/${linkId}/shared`, { channel }).catch(() => {});
  const copy = () => navigator.clipboard.writeText(url).then(() => { toast.success(t("b12.link.copied")); log("copy"); }).catch(() => toast.error(t("b12.link.copyFail")));
  return (
    <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50 p-3" data-testid="secure-link-share">
      <p className="text-xs font-semibold text-teal-900">{t("b12.link.onceOnly")}</p>
      <div className="flex gap-2"><Input readOnly value={url} onFocus={(e) => e.target.select()} className="bg-white font-mono text-xs" data-testid="secure-link-url" />
        <Button onClick={copy} data-testid="secure-link-copy"><Copy className="mr-1.5 h-4 w-4" />{t("b12.link.copy")}</Button></div>
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm"><a href={`sms:?&body=${encodeURIComponent(msg)}`} onClick={() => log("sms")} data-testid="secure-link-sms"><Smartphone className="mr-1.5 h-4 w-4" />SMS</a></Button>
        <Button asChild variant="outline" size="sm"><a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" onClick={() => log("whatsapp")} data-testid="secure-link-whatsapp"><MessageCircle className="mr-1.5 h-4 w-4" />WhatsApp</a></Button>
      </div>
    </div>
  );
}

const EMAIL_TOAST = { sent: ["success", "b15.email.sent"], failed: ["error", "b15.email.failed"], not_configured: ["warning", "b15.email.not_configured"], no_email: ["warning", "b12.link.noEmail"] };

function LinkDialog({ kind, refId, onClose, onDone }) {
  const { garage } = useGarage();
  const { data, reload } = useFetch("/secure-links", { kind, ref_id: refId });
  const [email, setEmail] = useState(true);
  const [url, setUrl] = useState("");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const hasActive = (data || []).some((l) => l.status === "active");
  const create = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/secure-links", { kind, ref_id: refId, send_email: email });
      setUrl(r.url);
      setCreated(r);
      const [tone, key] = EMAIL_TOAST[r.email_status] || ["success", "b12.link.created"];
      toast[tone](t(key));
      reload();
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = (id) => api.post(`/secure-links/${id}/revoke`).then(() => { toast.success(t("b12.link.revoked")); setUrl(""); reload(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="secure-link-dialog">
        <DialogHeader><DialogTitle>{t("b12.link.title")}</DialogTitle><DialogDescription>{t(`b12.link.hint_${kind}`)}</DialogDescription></DialogHeader>
        {url ? <><ShareBox url={url} kind={kind} linkId={created.link.id} />{created.email_status && <p className={`text-xs font-semibold ${created.email_status === "sent" ? "text-emerald-700" : "text-amber-700"}`} data-testid="secure-link-email-status">{t(`b15.email.${created.email_status}`)}</p>}</> : (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={email} onCheckedChange={setEmail} data-testid="secure-link-email-switch" />{t("b12.link.alsoEmail")}</label>
            {hasActive && <p className="text-xs text-amber-700">{t("b12.link.regenWarn")}</p>}
            <Button disabled={busy} onClick={create} data-testid="secure-link-create">{hasActive ? t("b12.link.regenerate") : t("b12.link.create")}</Button>
          </div>
        )}
        <div className="mt-2 space-y-1.5" data-testid="secure-link-list">
          {(data || []).map((l) => (
            <div key={l.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-xs" data-testid={`secure-link-row-${l.id}`}>
              <Badge tone={TONE[l.status]}>{t(`b12.link.st.${l.status}`)}</Badge>
              <span className="min-w-0 flex-1 truncate font-mono text-slate-400">••••••••</span>
              <span className="text-slate-500">{l.used_at ? t(`b12.link.act.${l.used_action}`) : t("b12.link.expires", { date: fmtDateTime(l.expires_at, garage.timezone) })}{l.email_status && ` · ${t(`b15.email.${l.email_status}`)}`}</span>
              {l.status === "active" && <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => revoke(l.id)} data-testid={`secure-link-revoke-${l.id}`}>{t("b12.link.revoke")}</Button>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SecureLinkButton({ kind, refId, variant = "outline", label, disabled, title, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} size={kind === "report" ? "sm" : "default"} disabled={disabled} title={title} onClick={() => setOpen(true)} data-testid={`secure-link-open-${kind}`}><Link2 className="mr-2 h-4 w-4" />{label || t("b12.link.send")}</Button>
      {open && <LinkDialog kind={kind} refId={refId} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}
